import React from "react";
import { Link } from "react-router-dom";
import { Checkbox, Tooltip, Dropdown } from "flowbite-react";
import { twMerge } from "tailwind-merge";
import { FaArrowRight, FaCakeCandles } from "react-icons/fa6";
import { HiLightningBolt } from "react-icons/hi";
import { AiOutlineLoading } from "react-icons/ai";
import moment from "moment";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import { RoleOptionsPart } from "./RoleOptionsPart";
import { TypeOptionsPart } from "./TypeOptionPart";
import { CustomButton } from "../../../atoms/CustomButton";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { USER_LIST_COMPONENT_TYPE } from "../../../../constants/types";
import { User, UserMatch } from "./UsersListComponent";
import * as flags from "country-flag-icons/react/3x2";
import NoTranslate from "../../../atoms/NoTranslate";
import { Copy } from "../../../atoms/Copy";

const calculateDaysUntilBirthday = (
  birthDate: string | null
): number | null => {
  if (!birthDate) return null;

  const today = moment().startOf("day");
  const birth = moment(birthDate);

  if (!birth.isValid()) return null;

  const thisYearBirthday = moment(birth).year(today.year());

  if (thisYearBirthday.isBefore(today, "day")) {
    const nextYearBirthday = moment(birth).year(today.year() + 1);
    return nextYearBirthday.diff(today, "days");
  }

  return thisYearBirthday.diff(today, "days");
};

const formatMatchPath = (
  path: string | undefined,
  unknownLabel: string
): string => {
  if (!path) return unknownLabel;
  return path
    .split(".")
    .map((segment) =>
      segment
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
    )
    .join(" › ");
};

const formatMatchValue = (
  value: any,
  labels: { empty: string; yes: string; no: string }
): string => {
  if (value === null || value === undefined) return labels.empty;
  if (typeof value === "boolean") return value ? labels.yes : labels.no;
  if (typeof value === "number") {
    if (value > 1_000_000_000_000 && value < 10_000_000_000_000) {
      return moment(value).format("lll");
    }
    return value.toLocaleString();
  }
  const str = String(value);
  return str.length > 60 ? str.slice(0, 57) + "..." : str;
};

interface UsersCardViewProps {
  users: Array<User>;
  selectedRowIds: Set<string>;
  toggleRowSelection: (id: string) => void;
  selectAll: boolean;
  onSelectAll: (checked: boolean) => void;
  type: USER_LIST_COMPONENT_TYPE;
  resRole: any;
  resType: any;
  isLoadingRole: boolean;
  isLoadingType: boolean;
  isErrorRole: boolean;
  isErrorType: boolean;
  isSuccessRole: boolean;
  isSuccessType: boolean;
  refetchRole: () => void;
  refetchType: () => void;
  canReadRole?: boolean;
  canReadType?: boolean;
  canSeeProfile?: boolean;
  sortOrder?: "ASC" | "DESC" | "";
  setSortOrder?: (value: "ASC" | "DESC" | "") => void;
  singleColumn?: boolean;
}

export const UsersCardView: React.FC<UsersCardViewProps> = ({
  users,
  selectedRowIds,
  toggleRowSelection,
  selectAll,
  onSelectAll,
  type,
  resRole,
  resType,
  isLoadingRole,
  isLoadingType,
  isErrorRole,
  isErrorType,
  isSuccessRole,
  isSuccessType,
  refetchRole,
  refetchType,
  canReadRole = true,
  canReadType = true,
  canSeeProfile = true,
  sortOrder,
  setSortOrder,
  singleColumn = false,
}) => {
  const [t] = useTranslation();
  const { user, customer_identification, userEndedSubscription } = useAuth();

  return (
    <div>
      {users && users.length > 0 && (
        <div className="mb-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Checkbox
              onChange={(e) => onSelectAll(e.target.checked)}
              checked={selectAll}
              className="appearance-none checked:bg-blue-600"
            />
            <span className="text-sm font-medium text-gray-700">
              {selectAll
                ? t("dashboard.usersList.deselectAll")
                : t("dashboard.usersList.selectAll")}
            </span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {setSortOrder && (
              <Dropdown
                label=""
                dismissOnClick
                placement="bottom-end"
                renderTrigger={() => (
                  <CustomButton
                    variant="secondary"
                    title={
                      !sortOrder
                        ? t("dashboard.usersList.item-07")
                        : sortOrder === "ASC"
                        ? t("dashboard.usersList.item-08")
                        : t("dashboard.usersList.item-09")
                    }
                    icon="chevronDown"
                  />
                )}
              >
                {["ASCENDING", "DESCENDING"].map((item: any) => (
                  <Dropdown.Item
                    key={item}
                    onClick={() =>
                      setSortOrder(
                        item === "ASCENDING"
                          ? "ASC"
                          : item === "DESCENDING"
                          ? "DESC"
                          : ""
                      )
                    }
                  >
                    {item === "ASCENDING"
                      ? t("dashboard.usersList.item-08")
                      : t("dashboard.usersList.item-09")}
                  </Dropdown.Item>
                ))}
              </Dropdown>
            )}
          </div>
        </div>
      )}

      <div
        className={twMerge(
          "grid grid-cols-1 gap-4",
          !singleColumn && "md:grid-cols-2 xl:grid-cols-3"
        )}
      >
        {users?.map((userItem) => {
          const isSelected = selectedRowIds.has(userItem.user_identification);
          const isBanned = userItem.user_account_status === "BANNED";
          const isOk = userItem.user_account_status === "OK";
          const isCurrentUser =
            userItem.user_identification === customer_identification;

          const fullName =
            [userItem.user_first_name, userItem.user_last_name]
              .filter(Boolean)
              .join(" ") || userItem.user_user_name;

          let avatarInitials = "";
          if (userItem.user_first_name && userItem.user_last_name) {
            avatarInitials =
              userItem.user_first_name.charAt(0).toUpperCase() +
              userItem.user_last_name.charAt(0).toUpperCase();
          } else if (fullName) {
            avatarInitials = fullName.charAt(0).toUpperCase();
          } else {
            avatarInitials = userItem.user_email.charAt(0).toUpperCase();
          }

          const daysUntilBirthday = calculateDaysUntilBirthday(
            userItem.user_birth_date
          );

          const CountryFlag = userItem.user_country_code
            ? (flags as any)[userItem.user_country_code.toUpperCase()]
            : null;

          return (
            <div
              key={userItem.user_identification}
              className={`bg-white rounded-lg border transition-colors hover:border-gray-300 flex flex-col overflow-hidden relative ${
                isSelected ? "border-blue-500" : "border-gray-200"
              }`}
            >
              <div className="relative p-4 pb-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    onChange={() =>
                      toggleRowSelection(userItem.user_identification)
                    }
                    checked={isSelected}
                    id={userItem.user_identification}
                    className="relative z-10 mt-1 appearance-none checked:bg-blue-600 shrink-0"
                  />

                  <div className="shrink-0">
                    {userItem.user_avatar ? (
                      <img
                        src={userItem.user_avatar}
                        alt={fullName || userItem.user_email}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-xs">
                        <NoTranslate>{avatarInitials}</NoTranslate>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {CountryFlag && (
                      <div className="relative z-10 float-right ml-2">
                        <CountryFlag
                          className="w-8 h-6 rounded-sm shadow-sm border border-gray-200"
                          title={userItem.user_country_code || ""}
                        />
                      </div>
                    )}
                    <div className="mb-1">
                      {(() => {
                        const primary = fullName || userItem.user_email;
                        const target = isCurrentUser
                          ? "/control-panel/profile"
                          : `/control-panel/users/${userItem.user_identification}`;
                        const label = (
                          <span className="truncate">
                            <NoTranslate>{primary}</NoTranslate>
                          </span>
                        );
                        return (
                          <>
                            {isCurrentUser || canSeeProfile ? (
                              <Link
                                to={target}
                                state={
                                  isCurrentUser
                                    ? undefined
                                    : { navigateType: type }
                                }
                                title={primary}
                                className="group flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-blue-700 transition-colors min-w-0 after:absolute after:inset-0 after:content-['']"
                              >
                                {label}
                                <FaArrowRight className="w-2.5 h-2.5 shrink-0 text-gray-300 group-hover:text-blue-700 transition-colors" />
                              </Link>
                            ) : (
                              <div
                                className="text-sm font-semibold text-gray-900 truncate"
                                title={primary}
                              >
                                {label}
                              </div>
                            )}
                            {fullName && (
                              <div
                                className="text-xs text-gray-500 truncate mt-0.5"
                                title={userItem.user_email}
                              >
                                <NoTranslate>{userItem.user_email}</NoTranslate>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      <div className="relative z-10 flex flex-row flex-wrap gap-2 mt-2">
                        {!userItem.user_is_active && (
                          <StatusBadge color="warning">
                            {t("dashboard.usersList.item-18")}
                          </StatusBadge>
                        )}
                        {isBanned && (
                          <Tooltip content={t("dashboard.usersList.item-17")}>
                            <StatusBadge color="failure">
                              {t("dashboard.usersList.item-17")}
                            </StatusBadge>
                          </Tooltip>
                        )}
                        {isOk && (
                          <StatusBadge color="success" dot>
                            {t("dashboard.usersList.item-11")}
                          </StatusBadge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 flex-grow">
                <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                  {canReadRole && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.usersList.item-03")}
                      </dt>
                      <dd className="min-w-0">
                        {isLoadingRole && (
                          <AiOutlineLoading className="h-4 w-4 animate-spin text-gray-400" />
                        )}
                        {isErrorRole && (
                          <CustomButton
                            onClick={() => refetchRole()}
                            title="Reload"
                            size="xs"
                            variant="secondary"
                          />
                        )}
                        {isSuccessRole && (
                          <RoleOptionsPart
                            data={{ role: userItem.user_role }}
                            entity_identification={
                              user.entity_identification as string
                            }
                            user_identification={customer_identification}
                            customerId={userItem.user_identification}
                            list_role={resRole}
                            disabled={userEndedSubscription}
                          />
                        )}
                      </dd>
                    </>
                  )}

                  {canReadType && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.usersList.item-04")}
                      </dt>
                      <dd className="min-w-0">
                        {isLoadingType && (
                          <AiOutlineLoading className="h-4 w-4 animate-spin text-gray-400" />
                        )}
                        {isErrorType && (
                          <CustomButton
                            onClick={() => refetchType()}
                            title="Reload"
                            size="xs"
                            variant="secondary"
                          />
                        )}
                        {isSuccessType && (
                          <TypeOptionsPart
                            data={{ type: userItem.user_type }}
                            entity_identification={
                              user.entity_identification as string
                            }
                            user_identification={customer_identification}
                            customerId={userItem.user_identification}
                            list_type={resType}
                            disabled={userEndedSubscription}
                          />
                        )}
                      </dd>
                    </>
                  )}

                  <dt className="text-gray-500">
                    {t("dashboard.usersList.item-15")}
                  </dt>
                  <dd className="flex min-w-0 items-center gap-1.5 font-mono text-gray-700">
                    <span className="truncate">
                      <NoTranslate>
                        {userItem.user_identification?.substring(0, 5) +
                          "..." +
                          userItem.user_identification?.slice(
                            userItem.user_identification.length - 5
                          )}
                      </NoTranslate>
                    </span>
                    <Copy
                      target={userItem.user_identification}
                      iconOnly
                      title={t("dashboard.usersList.copyId") as string}
                      className="rounded p-0.5 transition-colors hover:bg-gray-100"
                      iconClassName="h-3 w-3"
                    />
                  </dd>

                  {userItem.user_phone && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.usersList.phone")}
                      </dt>
                      <dd className="truncate text-gray-700">
                        <NoTranslate>{userItem.user_phone}</NoTranslate>
                      </dd>
                    </>
                  )}

                  <dt className="text-gray-500">
                    {t("dashboard.usersList.item-05")}
                  </dt>
                  <dd className="tabular-nums text-gray-700">
                    {moment
                      .utc(userItem.user_creation_date_time)
                      .local()
                      .format("ll")}
                  </dd>

                  {userItem.user_last_connection && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.usersList.lastConnection")}
                      </dt>
                      <dd className="text-gray-700">
                        {moment
                          .utc(userItem.user_last_connection)
                          .local()
                          .fromNow()}
                      </dd>
                    </>
                  )}

                  {daysUntilBirthday !== null && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.usersList.birthday")}
                      </dt>
                      <dd className="text-gray-700">
                        {daysUntilBirthday <= 7 ? (
                          <span
                            className={twMerge(
                              "inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-medium",
                              "bg-amber-50 text-amber-700",
                              daysUntilBirthday === 1 &&
                                "bg-amber-100 text-amber-800",
                              daysUntilBirthday === 0 &&
                                "bg-amber-500 font-semibold text-amber-950 motion-safe:animate-pulse"
                            )}
                          >
                            <FaCakeCandles
                              className="h-3 w-3 shrink-0"
                              aria-hidden="true"
                            />
                            {daysUntilBirthday === 0
                              ? t("dashboard.usersList.birthdayToday")
                              : daysUntilBirthday === 1
                              ? t("dashboard.usersList.birthdayTomorrow")
                              : t("dashboard.usersList.inDays", {
                                  days: daysUntilBirthday,
                                })}
                          </span>
                        ) : (
                          <span className="tabular-nums">
                            {moment(userItem.user_birth_date).format("ll")}
                          </span>
                        )}
                      </dd>
                    </>
                  )}
                </dl>
              </div>

              {userItem.matches && userItem.matches.length > 0 && (
                <div className="px-4 pb-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 overflow-hidden">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <HiLightningBolt className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span className="text-xs font-semibold text-amber-700">
                        {t("dashboard.usersList.matchedIn")}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {userItem.matches.map(
                        (match: UserMatch, mIdx: number) => {
                          const matchedContent = match.value;
                          const matchLabel = t(
                            "dashboard.usersList.matchedInValue"
                          );
                          const valueLabels = {
                            empty: t("dashboard.usersList.matchEmpty"),
                            yes: t("dashboard.usersList.matchYes"),
                            no: t("dashboard.usersList.matchNo"),
                          };

                          return (
                            <div key={mIdx} className="text-xs overflow-hidden">
                              <div
                                className="text-amber-600 font-medium truncate"
                                title={match.path || match.field}
                              >
                                {formatMatchPath(
                                  match.path || match.field,
                                  t("dashboard.usersList.matchUnknownField")
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                <span className="text-amber-400 shrink-0">
                                  →
                                </span>
                                <span
                                  className="text-amber-800 font-mono bg-amber-100 px-1.5 py-0.5 rounded truncate block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                                  title={String(matchedContent ?? "")}
                                >
                                  <NoTranslate>
                                    {formatMatchValue(
                                      matchedContent,
                                      valueLabels
                                    )}
                                  </NoTranslate>
                                </span>
                                <span className="text-amber-400 italic text-xs shrink-0">
                                  ({matchLabel})
                                </span>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
